import { Grid, InputAdornment, Stack, styled, Typography } from '@mui/material';
import { InputText } from '../../../../components/elements/InputText';
import { Controller, useFormContext } from 'react-hook-form';
import { InputNumber } from '../../../../components/elements/InputNumber';
import { RadioGroupCustom } from '../../../../components/elements/RadioGroupCustom';
import { optionArrearsOther } from 'const/option';
import { InputSelect } from '../../../../components/elements/InputSelect';
import { ClientOption, CreateFiles } from './FileForm/hook';
import CustomDatePicker from '../../../../components/elements/InputDate';
import dayjs from 'dayjs';
import MultiSelectAutocomplete from '../../../../components/elements/MultiSelectAutocomplete';
import React from 'react';
import { TypeOption } from '../../NewFileSetUpPage/hook';
import { StyledParams } from 'types/common';
import theme from '../../../../../styles/theme';
import { IconMoney } from '../../../../../assets/icon';

type InfoDetailFileProps = {
  isCompleted: boolean;
  optionClient?: ClientOption[];
  optionClientChild?: ClientOption[];
  optionAuctionDeliveryLocation?: TypeOption[];
  changeOptionClient?: (value: number) => void;
  optionsUser: TypeOption[];
  createdAt?: string;
  isUser: boolean;
};

export const InfoDetailFile = ({
  isCompleted,
  optionClient,
  optionClientChild,
  optionAuctionDeliveryLocation,
  changeOptionClient,
  optionsUser,
  isUser,
  createdAt
}: InfoDetailFileProps) => {
  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useFormContext<CreateFiles>();
  const watchArrears = watch('arrears');
  return (
    <Stack gap={4.5} width={'100%'} flexDirection={'row'}>
      <Grid container spacing={4}>
        <Grid container spacing={2.5} item xs={12} sm={12} md={12} lg={12}>
          <Grid item xs={12} md={6}>
            <Controller
              name={'clientOptionsId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSelect
                  inputError={errors.clientOptionsId?.message}
                  style={{ height: 50 }}
                  title={'Client Name'}
                  placeholder={'Client'}
                  options={
                    optionClient?.map((item: ClientOption) => ({
                      value: item.id,
                      label: item.value
                    })) || []
                  }
                  disabled={isCompleted}
                  value={value}
                  onChange={(event) => {
                    changeOptionClient && changeOptionClient(event.target.value as number);
                    setValue('clientOptionsChildId', null);
                    onChange(event);
                  }}
                />
              )}
            />
          </Grid>
          {optionClientChild?.length !== 0 && (
            <Grid item xs={12} md={6}>
              <Controller
                name={'clientOptionsChildId'}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSelect
                    inputError={errors.clientOptionsChildId?.message}
                    style={{ height: 50 }}
                    title={'Client Children'}
                    placeholder={'Client Children'}
                    options={
                      optionClientChild?.map((item: ClientOption) => ({
                        value: item.id,
                        label: item.value
                      })) || []
                    }
                    disabled={isCompleted}
                    value={value}
                    onChange={(event) => {
                      onChange(event);
                    }}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <InputText
              title={'Client File Number'}
              inputError={errors.fileNumber?.message}
              placeholder={'Client File Number'}
              inputProps={{
                ...register('fileNumber'),
                disabled: isCompleted
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText
              title={'Last Name'}
              inputError={errors.lastName?.message}
              placeholder={'Last Name'}
              inputProps={{
                ...register('lastName'),
                disabled: isCompleted
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText
              title={'First Name'}
              inputError={errors.firstName?.message}
              placeholder={'First Name'}
              inputProps={{
                ...register('firstName'),
                disabled: isCompleted
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name={'auctionDeliveryLocationId'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSelect
                  inputError={errors.auctionDeliveryLocationId?.message}
                  style={{ height: 50 }}
                  title={'Auction Delivery Location'}
                  placeholder={'Auction Delivery Location'}
                  options={optionAuctionDeliveryLocation}
                  value={value}
                  disabled={isCompleted}
                  onChange={(event) => {
                    onChange(event);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item container xs={12} md={6}>
            <Grid item xs={12} md={12}>
              <Typography fontSize={'13px'} fontWeight={500} mb={1} color={theme.colors.dark1}>
                Year/Make/Model
              </Typography>
            </Grid>
            <Grid container spacing={1.25} item xs={12} md={12}>
              <Grid item xs={12} md={4}>
                <InputText
                  inputError={errors.year?.message}
                  placeholder={'Year'}
                  inputProps={{
                    ...register('year'),
                    disabled: isCompleted
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputText
                  inputError={errors.make?.message}
                  placeholder={'Make'}
                  inputProps={{
                    ...register('make'),
                    disabled: isCompleted
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputText
                  inputError={errors.model?.message}
                  placeholder={'Model'}
                  inputProps={{
                    ...register('model'),
                    disabled: isCompleted
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText
              title={'VIN'}
              inputError={errors.vin?.message}
              placeholder={'VIN'}
              inputProps={{
                ...register('vin'),
                disabled: isCompleted
              }}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name={'arrears'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputNumber
                  title={'Arrears'}
                  inputError={errors.arrears?.message}
                  placeholder={'Arrears'}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconMoney />
                    </InputAdornment>
                  }
                  inputProps={{
                    disabled: isCompleted
                  }}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
          {watchArrears === 0 && (
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <RadioGroupCustom
                    onChange={onChange}
                    value={value || ''}
                    label={'Options'}
                    options={optionArrearsOther}
                  />
                )}
                name={'arrearsOther'}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <Controller
              name={'contractBalance'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputNumber
                  title={'Contract Balance'}
                  inputError={errors.contractBalance?.message}
                  placeholder={'Contract Balance'}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconMoney />
                    </InputAdornment>
                  }
                  inputProps={{
                    disabled: isCompleted
                  }}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name={'contactDate'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomDatePicker
                  inputError={errors.contactDate?.message}
                  title={'Contract Date'}
                  value={value ? dayjs(value) : null}
                  onChange={onChange}
                  datePickerProps={{
                    disabled: isCompleted,
                    maxDate: dayjs()
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText
              title={'PPSA Base Registration Number'}
              inputError={errors.ppsaBaseRegistrationNumber?.message}
              placeholder={'PPSA Base Registration Number'}
              inputProps={{
                ...register('ppsaBaseRegistrationNumber'),
                disabled: isCompleted
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name={'ppsaRegistrationDate'}
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomDatePicker
                  inputError={errors.ppsaRegistrationDate?.message}
                  title={'PPSA Registration Date'}
                  value={value ? dayjs(value) : null}
                  onChange={onChange}
                  datePickerProps={{
                    disabled: isCompleted,
                    maxDate: dayjs()
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText
              title={'Co-Borrower First Name'}
              inputError={errors.coBorrowerFirstName?.message}
              placeholder={'Co-Borrower First Name'}
              inputProps={{
                ...register('coBorrowerFirstName'),
                disabled: isCompleted
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText
              title={'Co-Borrower Last Name'}
              inputError={errors.coBorrowerLastName?.message}
              placeholder={'Co-Borrower Last Name'}
              inputProps={{
                ...register('coBorrowerLastName'),
                disabled: isCompleted
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText
              title={'Plate'}
              inputError={errors.plate?.message}
              placeholder={'Plate'}
              inputProps={{
                ...register('plate'),
                disabled: isCompleted
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputText
              title={'Colour'}
              inputError={errors.colour?.message}
              placeholder={'Colour'}
              inputProps={{
                ...register('colour'),
                disabled: isCompleted
              }}
            />
          </Grid>
          {!isUser && (
            <>
              <Grid item xs={12} md={12}>
                <Stack flexDirection={'column'} gap={2.5} width={'100%'}>
                  <Stack
                    style={{
                      background: '#F0F1F2',
                      borderRadius: '10px',
                      padding: '20px 30px',
                      gap: 10,
                      minHeight: '120px'
                    }}>
                    <Typography style={{ fontSize: '14px', fontWeight: '400', lineHeight: '20px' }}>
                      Quick Notes
                    </Typography>
                    <InputText
                      inputError={errors.quickNote?.message}
                      placeholder={'Quick Note'}
                      inputProps={{
                        ...register('quickNote'),
                        sx: {
                          '& .MuiInputBase-input': {
                            backgroundColor: 'transparent !important'
                          }
                        },
                        disabled: isCompleted,
                        multiline: true,
                        rows: 4,
                        style: {
                          background: 'transparent',
                          padding: 0,
                          backgroundColor: 'transparent'
                        }
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={12}>
                <Stack
                  style={{
                    background: '#F0F1F2',
                    borderRadius: '10px',
                    padding: '20px 30px',
                    gap: 22,
                    minHeight: '240px'
                  }}>
                  <StackTime>
                    <img
                      src={'/icons/icon_date_time.svg'}
                      alt={'icon-date'}
                      style={{ width: 20, height: 20 }}
                    />
                    <TextTimeStyled>{dayjs(createdAt).format('YYYY/MM/DD')}</TextTimeStyled>
                  </StackTime>
                  <Controller
                    name={'userAssign'}
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <MultiSelectAutocomplete
                          value={value}
                          label={'Assigned to'}
                          onChange={(_, value: TypeOption[]) => {
                            onChange(value);
                          }}
                          options={optionsUser}
                        />
                      );
                    }}
                  />
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};

const StackTime = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '5px'
}));
const TextTimeStyled = styled(Typography)(({ theme }: StyledParams) => ({
  color: theme.colors.dark2,
  fontWeight: 500,
  lineHeight: '16.8px'
}));
